import React, { useRef, useState } from "react";
import logo from '../../assets/logo.jpeg';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { verifyResetToken } from "../../api/reset-auth-api";

const VerifyCode = () => {
  const inputs = useRef([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (/^\d$/.test(value)) {
      if (inputs.current[index + 1]) {
        inputs.current[index + 1].focus();
      }
    } else {
      e.target.value = ""; // invalid input
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    const token = inputs.current.map(input => input?.value || "").join("");
    // Assuming a 6-digit token as per backend `requestReset`
    if (token.length !== 6) { // Changed from 5 to 6 digits
      setError("Please enter all 6 digits of the code.");
      setLoading(false);
      return;
    }

    try {
      const response = await verifyResetToken({ email, resetCode: token });
      setSuccess("Code verified successfully! Redirecting...");
      
      // ✅ Store email and resetCode in distinct keys for the standard reset flow
      localStorage.setItem("resetEmail", email);
      localStorage.setItem("resetTokenValue", token); 

      setTimeout(() => {
        navigate("/change-password"); // Redirect to the unified change password page
      }, 2000);
    } catch (err) {
      setError(err.message || "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  };

  // --- UI remains largely the same ---
  // 🔄 Loading UI
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Verifying reset code...</p>
        </div>
      </div>
    );
  }

  // ❌ Error UI
  if (error && !success) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="bg-white border border-red-300 shadow-md rounded p-6 w-full max-w-md text-center">
          <div className="text-red-500 text-4xl mb-2">⚠️</div>
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Verification Failed
          </h2>
          <p className="text-gray-700">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ✅ Success UI
  if (success) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="bg-white border border-green-300 shadow-md rounded p-6 w-full max-w-md text-center">
          <div className="text-green-500 text-4xl mb-2">✅</div>
          <h2 className="text-xl font-semibold text-green-600 mb-2">
            Code Verified
          </h2>
          <p className="text-gray-700">{success}</p>
          <p className="text-sm text-gray-500 mt-2">Redirecting to set new password...</p>
        </div>
      </div>
    );
  }

  // 👇 Main UI
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md bg-white p-8 shadow-md rounded text-center">
        <div className="flex justify-center mb-6">
          <img src={logo} alt="Hold the Child" className="h-16" />
        </div>

        <h2 className="text-xl font-semibold text-gray-700 mb-2">Password Reset</h2>
        <p className="text-sm text-gray-600 mb-6">
          Enter the 6-digit code sent to <span className="font-semibold">{email}</span>
        </p>

        <div className="flex justify-center space-x-2 mb-6">
          {[...Array(6)].map((_, i) => ( // Changed to 6 inputs
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              maxLength={1}
              className="w-12 h-12 text-center text-lg border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-400"
              onChange={(e) => handleChange(e, i)}
              type="text"
              inputMode="numeric"
              pattern="\d*"
            />
          ))}
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 rounded"
        >
          Proceed
        </button>

        <div className="border-t border-gray-200 mt-6"></div>

        <Link to="/" className="text-sm text-gray-500 hover:underline mt-4 block">
          Cancel
        </Link>
      </div>
    </div>
  );
};

export default VerifyCode;
